/**
 * As operações SQL: as consultas e escritas guardadas que leem e gravam no
 * banco de um cliente. Só a Cliqfy vê.
 *
 * - A lista mostra todas, com filtro pela finalidade e busca pelo nome.
 * - No topo, as escritas que ainda esperam aprovação, de antes do ADR-0035: hoje
 *   a escrita entra no ar ao salvar, e qualquer um da Cliqfy aprova as que sobraram.
 * - Abrir leva ao editor; sem conexão, não há o que escrever, e a tela manda
 *   cadastrar uma em Integrações.
 */

import { useState, type ReactNode } from 'react';
import { useConexoesDeDados, useTodasAsOperacoesDeDados } from '@/shared/api/consultas';
import { useSessao } from '@/shared/sessao/contexto';
import { Botao, CabecalhoDeTela, Campo, FiltroPilula, Icone, Selo, Tabela } from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import {
  FINALIDADES,
  esperamMinhaAprovacao,
  filtrar,
  situacaoDe,
  travar,
  type Finalidade,
  type Operacao,
} from '../lib/operacoes';
import { EditorDaOperacao } from './editor-da-operacao';
import './operacoes.css';

export function Operacoes({
  id,
  nova,
  aoAbrir,
  aoNova,
  aoVoltarParaALista,
  aoIrParaIntegracoes,
}: {
  /** A operação aberta no editor, ou `null` na lista. */
  id: string | null;
  nova: boolean;
  aoAbrir: (id: string) => void;
  aoNova: () => void;
  aoVoltarParaALista: () => void;
  aoIrParaIntegracoes: () => void;
}): ReactNode {
  if (nova || id) {
    return (
      <EditorDaOperacao
        key={id ?? 'nova'}
        id={id}
        aoVoltar={aoVoltarParaALista}
        aoCriada={aoAbrir}
        aoIrParaIntegracoes={aoIrParaIntegracoes}
      />
    );
  }
  return (
    <ListaDeOperacoes aoAbrir={aoAbrir} aoNova={aoNova} aoIrParaIntegracoes={aoIrParaIntegracoes} />
  );
}

const ORDEM_DAS_FINALIDADES: readonly Finalidade[] = ['consulta', 'listagem', 'acao'];

function ListaDeOperacoes({
  aoAbrir,
  aoNova,
  aoIrParaIntegracoes,
}: {
  aoAbrir: (id: string) => void;
  aoNova: () => void;
  aoIrParaIntegracoes: () => void;
}): ReactNode {
  const operacoes = useTodasAsOperacoesDeDados(true);
  const conexoes = useConexoesDeDados();
  const { estado } = useSessao();
  const meuId = estado.nome === 'autenticado' ? estado.me.usuario.id : null;
  const papel = estado.nome === 'autenticado' ? estado.me.papel : null;
  const [finalidade, definirFinalidade] = useState<Finalidade | null>(null);
  const [busca, definirBusca] = useState('');

  const semConexao = conexoes.data !== undefined && conexoes.data.itens.length === 0;
  const nomeDaConexao = (id: string): string =>
    conexoes.data?.itens.find((c) => c.id === id)?.nome ?? 'Conexão removida';

  return (
    <div className="operacoes">
      <CabecalhoDeTela
        titulo="Operações SQL"
        retrato={
          <span className="operacoes__icone-do-titulo" aria-hidden="true">
            <Icone nome="planilha" />
          </span>
        }
        apoio="O SQL guardado que lê e grava no banco de um cliente. As listas e as automações usam o que está publicado aqui."
        acoes={
          <>
            <Selo tom="info">Só Cliqfy</Selo>
            <Botao
              variante="primario"
              onClick={aoNova}
              {...travar(semConexao ? 'Cadastre uma conexão em Integrações antes.' : null)}
            >
              Nova operação
            </Botao>
          </>
        }
      />

      <div className="operacoes__conteudo">
        <Estado consulta={operacoes} forma="lista" linhas={4}>
          {(dados) => {
            if (dados.itens.length === 0) {
              return semConexao ? (
                <Vazio
                  titulo="Nenhuma conexão ainda"
                  porque="Uma operação lê de uma conexão ao banco do cliente. Cadastre e teste a conexão em Integrações primeiro."
                  acao={{ rotulo: 'Cadastrar uma conexão', aoClicar: aoIrParaIntegracoes }}
                />
              ) : (
                <Vazio
                  titulo="Nenhuma operação ainda"
                  porque="Uma operação é um SQL guardado, com os parâmetros e as colunas que ele devolve. A leitura e a escrita entram no ar ao salvar."
                  acao={{ rotulo: 'Escrever a primeira', aoClicar: aoNova }}
                />
              );
            }
            const minhas = esperamMinhaAprovacao(dados.itens, meuId, papel);
            const visiveis = filtrar(dados.itens, finalidade, busca);
            return (
              <>
                {minhas.length > 0 && (
                  <div className="operacoes__aprovar" role="status">
                    <Icone nome="alerta" />
                    <p>
                      <strong>
                        {minhas.length === 1
                          ? '1 escrita espera a sua aprovação:'
                          : `${minhas.length} escritas esperam a sua aprovação:`}
                      </strong>{' '}
                      {minhas.map((o, i) => (
                        <span key={o.id}>
                          {i > 0 && ', '}
                          <button
                            type="button"
                            className="operacoes__link"
                            onClick={() => aoAbrir(o.id)}
                          >
                            {o.nome}
                          </button>
                        </span>
                      ))}
                    </p>
                  </div>
                )}

                <div className="operacoes__ferramentas">
                  <div className="operacoes__filtros" role="group" aria-label="Finalidade">
                    <FiltroPilula
                      ligado={finalidade === null}
                      aoAlternar={() => definirFinalidade(null)}
                      contagem={dados.itens.length}
                    >
                      Todas
                    </FiltroPilula>
                    {ORDEM_DAS_FINALIDADES.map((f) => (
                      <FiltroPilula
                        key={f}
                        ligado={finalidade === f}
                        aoAlternar={() => definirFinalidade(finalidade === f ? null : f)}
                        contagem={dados.itens.filter((o) => o.finalidade === f).length}
                      >
                        {FINALIDADES[f].rotulo}
                      </FiltroPilula>
                    ))}
                  </div>
                  <div className="operacoes__busca">
                    <Campo
                      type="search"
                      icone="buscar"
                      rotuloOculto="Buscar pelo nome"
                      placeholder="Buscar pelo nome"
                      value={busca}
                      onChange={(e) => definirBusca(e.target.value)}
                    />
                  </div>
                </div>

                {visiveis.length === 0 ? (
                  <p className="operacoes__nota">Nenhuma operação com esse filtro.</p>
                ) : (
                  <Tabela
                    rotulo="Operações SQL"
                    colunas={[
                      { chave: 'nome', rotulo: 'Nome' },
                      { chave: 'conexao', rotulo: 'Conexão' },
                      { chave: 'tipo', rotulo: 'Tipo' },
                      { chave: 'finalidade', rotulo: 'Finalidade' },
                      { chave: 'situacao', rotulo: 'Situação' },
                      { chave: 'acoes', rotulo: 'Ações', rotuloOculto: true },
                    ]}
                    linhas={visiveis.map((o) => linhaDe(o, nomeDaConexao(o.conexao_id), aoAbrir))}
                    chaveDe={(linha) => String(linha['id'])}
                  />
                )}
              </>
            );
          }}
        </Estado>
      </div>
    </div>
  );
}

function linhaDe(
  o: Operacao,
  conexao: string,
  aoAbrir: (id: string) => void,
): Record<string, ReactNode> {
  const situacao = situacaoDe(o);
  return {
    id: o.id,
    nome: (
      <span className="operacoes__nome">
        {o.nome}
        <span className="operacoes__versao">v{o.versao}</span>
      </span>
    ),
    conexao,
    tipo: o.tipo === 'escrita' ? 'Escrita' : 'Leitura',
    finalidade: FINALIDADES[o.finalidade].rotulo,
    situacao: <Selo tom={situacao.tom}>{situacao.rotulo}</Selo>,
    acoes: (
      <Botao
        variante="secundario"
        miudo
        onClick={() => aoAbrir(o.id)}
        aria-label={`Abrir ${o.nome}`}
      >
        Abrir
      </Botao>
    ),
  };
}
